import { ChangeEvent } from 'react';
import { Iobject, Ioption, IopTrs, Itrans } from './type';

// #region util ---------------------------------------------------------------------------
export const isUndefined = (value: unknown): value is undefined =>
  typeof value === 'undefined';
export const isNull = (value: unknown): value is null => value === null;
export const isString = (value: unknown): value is string =>
  typeof value === 'string';
export const isNumber = (value: unknown): value is number =>
  typeof value === 'number';
export const isBlob = (value: unknown): value is Blob => value instanceof Blob;
export const isArray = <T = unknown>(value: unknown): value is Array<T> =>
  Array.isArray(value);
export const isObject = <T = unknown>(value: unknown): value is Iobject<T> =>
  typeof value === 'object';
export const isDate = (value: unknown): value is Date =>
  value instanceof Date ||
  (typeof value === 'object' &&
    Object.prototype.toString.call(value) === '[object Date]');

/** 產生亂數文字 */
export const generateRandomString = (length = 12): string => {
  const allCapsAlpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
  return [...Array(length)]
    .map(() => allCapsAlpha[Math.floor(Math.random() * allCapsAlpha.length)])
    .join('');
};

/** Class 多重相加 */
export const classCombine = (...args: (string | boolean | undefined)[]) => {
  const classArray = args.filter((v) => !!v);
  return classArray.length ? classArray.join(' ') : undefined;
};

/** option to trans */
export const option2Trans = <V extends Ioption = Ioption, T = undefined>(
  options: V[],
  other?: T,
): IopTrs<V, T> => {
  const trans: Itrans = {};
  const transOption: Itrans<V> = {};
  options.forEach((obj) => {
    trans[obj.value] = obj.label;
    transOption[obj.value] = obj;
  });
  return { options, trans, transOption, other };
};
// #endregion util

// #region Dataproccess ---------------------------------------------------------------------------
export const zeroFirst = (numValue: number, sliceNum = -3): string =>
  numValue < 0 ? String(numValue) : `0000${numValue}`.slice(sliceNum);

/** 貨幣 千分位數 */
export const toCurrency =
  (fix?: number) =>
  (value: string | number = 0): string => {
    // API回傳有時候會轉好(如 20,000) 所以先過濾掉 ',' ，不然 20,000 parseFloat會轉成20
    const data = parseFloat(String(value).replace(',', ''));
    if (Number.isNaN(data)) {
      return '';
    }

    let val = data.toString();
    if (fix && isNumber(fix)) {
      const fixTemp = Math.round(Math.abs(fix));
      val = parseFloat(val).toFixed(fixTemp);
    }

    const parts = val.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return parts.join('.');
  };
// #endregion Dataproccess

// #region onchange --------------------------------------------------------------------
export const inputVal = (value: unknown): string =>
  isNull(value) || isUndefined(value) ? '' : String(value);

export const strEmpty = (value: unknown): boolean => inputVal(value) === '';

/** input onChange */
export const inputChange =
  (updateData?: (x: Iobject) => void) =>
  (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): Iobject => {
    const updateObj = {
      [e.target.name]: e.target.value,
    };
    if (updateData) updateData(updateObj);

    return updateObj;
  };

// for Checkbox
export enum YNEnum {
  是 = 'Y',
  否 = 'N',
}
export const YNbool: [string, string] = [YNEnum.是, YNEnum.否];
/** Checkbox onChange */
export const checkboxChange =
  (
    updateData?: (x: Iobject) => void,
    trueVal: [string, string] | [true, false] = [true, false],
  ) =>
  (e: ChangeEvent<HTMLInputElement>): Iobject => {
    const updateObj = {
      [e.target.name]: e.target.checked ? trueVal[0] : trueVal[1],
    };
    if (updateData) updateData(updateObj);
    return updateObj;
  };

/** Select onChange */
export const selectChange =
  (updateData?: (x: Iobject) => void) =>
  (e: ChangeEvent<{ name: string; value: string }>): Iobject => {
    const updateObj = {
      [e.target.name]: e.target.value,
    };
    if (updateData) {
      updateData(updateObj);
    }
    return updateObj;
  };

export const nameValueChange =
  (updateData?: (x: Iobject) => void) =>
  ({ name, value }: { name: string; value: unknown }): Iobject => {
    const updateObj = { [name]: value };
    if (updateData) updateData(updateObj);
    return updateObj;
  };

/** DateRangePicker onChange */
export const dateRangeChange =
  (updateData?: (x: Iobject) => void) =>
  ({
    startName,
    startValue,
    endName,
    endValue,
  }: {
    startName?: string;
    startValue?: Date | null;
    endName?: string;
    endValue?: Date | null;
  }): Iobject => {
    const updateObj = {
      ...(startName ? { [startName]: startValue } : {}),
      ...(endName ? { [endName]: endValue } : {}),
    };
    if (updateData) updateData(updateObj);
    return updateObj;
  };

// #endregion onchange
